import MarkdownIt from 'markdown-it';

/**
 * Markdown handling for task descriptions.
 *
 * Descriptions live in two shapes: Tiptap HTML (anything saved through the
 * editor) and Markdown (Jira import via the backend ADF converter, AI output).
 * `toEditorContent` is the display gate the editor feeds from — HTML passes
 * through, everything else is converted here.
 *
 * This used to be a hand-rolled line scanner. It was structurally mismatched
 * with what the backend converter emits: it mangled length-aware code fences,
 * dropped list continuation and numbering, left backslash-escaped structurals
 * (`\#`, `1\.`) showing their backslash, and passed raw HTML in the source
 * straight through to the DOM. A real CommonMark parser handles all of it.
 *
 * SECURITY — `html: false` is load-bearing. Raw HTML in the markdown source is
 * ESCAPED rather than emitted, so `<script>`, `<img onerror=…>` and attribute
 * injection (`[x](a"onmouseover="b`) cannot become markup at all. That closes
 * the hole at the source, which is why this path deliberately does NOT run
 * through `sanitizeHTML`: the shared allowlist has no `img`/`src`, so
 * sanitizing here would strip exactly the imported attachment images this
 * feature exists to display. Callers that inject this HTML into the live DOM
 * outside ProseMirror (the AI previews) sanitize at their own sink — there,
 * losing an image is cheaper than trusting model output.
 *
 * Link destinations are still filtered by markdown-it's default `validateLink`,
 * which rejects `javascript:`, `vbscript:`, `file:` and non-image `data:` URLs
 * while allowing the root-relative `/uploads/...` destinations the importer
 * writes for attachments.
 */
function createParser(breaks: boolean): MarkdownIt {
  const parser = new MarkdownIt({
    html: false,
    linkify: false,
    breaks,
    typographer: false,
  });

  // Prose authored elsewhere — open links in a new tab and never hand the
  // opener over to the destination.
  const renderLinkOpen =
    parser.renderer.rules.link_open ??
    ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));
  parser.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    tokens[idx].attrSet('target', '_blank');
    tokens[idx].attrSet('rel', 'noopener noreferrer');
    return renderLinkOpen(tokens, idx, options, env, self);
  };

  // The editor's StarterKit registers h1–h3 only, and the ADF converter happily
  // emits `####`–`######`. An out-of-schema <h4> is not an error — ProseMirror
  // quietly demotes it to a paragraph, so the heading would vanish as a
  // heading. Clamp instead, the way the previous renderer did.
  const clampHeading: NonNullable<typeof parser.renderer.rules.heading_open> = (
    tokens,
    idx,
    options,
    _env,
    self,
  ) => {
    const token = tokens[idx];
    if (Number(token.tag.slice(1)) > 3) token.tag = 'h3';
    return self.renderToken(tokens, idx, options);
  };
  parser.renderer.rules.heading_open = clampHeading;
  parser.renderer.rules.heading_close = clampHeading;

  return parser;
}

const descriptionParser = createParser(false);
const commentParser = createParser(true);

/** Convert markdown to HTML for the Tiptap editor */
export function markdownToHtml(md: string): string {
  return descriptionParser.render(md).trim();
}

/**
 * Comment bodies. Same rules, but a single newline becomes a <br>: comments are
 * stored as raw text and used to render under `whitespace-pre-wrap`, so without
 * this a hand-written two-line comment would collapse onto one line. (The same
 * choice GitHub makes for comment fields, and the reason descriptions — real
 * documents — keep CommonMark paragraph folding instead.)
 */
export function commentMarkdownToHtml(md: string): string {
  return commentParser.render(md).trim();
}

/**
 * What the editor displays. HTML (Tiptap's own output) passes through;
 * anything else — Jira-imported or otherwise Markdown/plain text — is
 * converted so links, lists and inlined `/uploads/` images actually render
 * instead of showing literal Markdown.
 */
export function toEditorContent(value: string | null | undefined): string {
  if (!value || !value.trim()) return '';
  if (value.trim().startsWith('<') && value.includes('>')) return value;
  return markdownToHtml(value);
}
